'use client';

import { useEffect, useRef } from 'react';
import { Heading } from '@highereducation/hen-ui';

const ONETRUST_POLL_INTERVAL_MS = 100;
const ONETRUST_POLL_MAX_TRIES = 20;

interface OneTrustNoticeProps {
  noticeId: string;
  noticeJsonUrl: string;
  heading: string;
}

declare global {
  interface Window {
    OneTrust?: {
      NoticeApi?: {
        Initialized?: Promise<void>;
        LoadNotices: (urls: string[]) => void;
      };
    };
  }
}

export default function OneTrustNotice(props: OneTrustNoticeProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const noticeJsonUrl = props.noticeJsonUrl;

    // Inject the OneTrust notice script (with required settings attribute), or reuse an existing tag.
    const scriptId = 'otprivacy-notice-script';
    let intervalID: ReturnType<typeof setInterval> | null = null;
    let appendedScript = false;

    const scheduleLoadNotices = () => {
      let triesCounter = 0;
      if (intervalID !== null) {
        clearInterval(intervalID);
        intervalID = null;
      }
      intervalID = setInterval(() => {
        const isOneTrustLoaded =
          typeof window.OneTrust !== 'undefined' &&
          !!window.OneTrust?.NoticeApi?.Initialized;

        if (isOneTrustLoaded) {
          void window.OneTrust!.NoticeApi!.Initialized!.then(() => {
            window.OneTrust!.NoticeApi!.LoadNotices([noticeJsonUrl]);
          });
        }

        if (isOneTrustLoaded || triesCounter === ONETRUST_POLL_MAX_TRIES) {
          if (intervalID !== null) {
            clearInterval(intervalID);
            intervalID = null;
          }
        }

        triesCounter += 1;
      }, ONETRUST_POLL_INTERVAL_MS);
    };

    const existingScript = document.getElementById(scriptId);
    if (existingScript) {
      scheduleLoadNotices();
    } else {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src =
        'https://privacyportal-cdn.onetrust.com/privacy-notice-scripts/otnotice-1.0.min.js';
      script.type = 'text/javascript';
      script.charset = 'UTF-8';
      script.setAttribute(
        'settings',
        'eyJjYWxsYmFja1VybCI6Imh0dHBzOi8vcHJpdmFjeXBvcnRhbC5vbmV0cnVzdC5jb20vcmVxdWVzdC92MS9wcml2YWN5Tm90aWNlcy9zdGF0cy92aWV3cyJ9',
      );
      script.async = true;
      script.onload = () => {
        scheduleLoadNotices();
      };
      document.body.appendChild(script);
      appendedScript = true;
    }

    return () => {
      if (intervalID !== null) {
        clearInterval(intervalID);
      }
      if (appendedScript) {
        const existing = document.getElementById(scriptId);
        if (existing?.parentNode) {
          existing.parentNode.removeChild(existing);
        }
      }
    };
  }, [props.noticeJsonUrl]);

  return (
    <div className='mx-auto flex w-full min-w-0 max-w-article-content flex-col gap-2xl min-h-[60vh]'>
      <Heading variant='h1'>{props.heading}</Heading>
      <div
        id={`otnotice-${props.noticeId}`}
        className='otnotice'
        ref={containerRef}
      />
    </div>
  );
}
